/**
 * Browser → PostHog Logs shipper.
 *
 * The app's warning-class signal (console.warn bridge, explicit warning
 * captures: Pusher reconnects, notification permission, SDK chatter) belongs
 * in Logs, not the error inbox, so this is the browser twin of the Node transport
 * in `packages/node/logger/src/posthog.ts`: the same OTLP/HTTP encoding (shared
 * via @mondaygpt/constants), batched, with a per-minute cap so a warn loop
 * can't burn the logs quota or the user's bandwidth.
 *
 * Nothing here may throw into the caller or log through console (the console
 * bridge would route a warning straight back here). Delivery failures are
 * reported through `onFailure`, which the owner throttles.
 */

import { otlpLogRecord, otlpLogsPayload, type OtlpLogRecord } from '@mondaygpt/constants'

export type PosthogLogLevel = 'error' | 'warn' | 'info' | 'debug'

const DEFAULT_BATCH_SIZE = 20
const DEFAULT_FLUSH_INTERVAL_MS = 5000
// Guardrail per tab: the console bridge already rate-limits identical
// messages (3/min); this caps the total so a fan-out of distinct warnings
// still stays cheap.
const DEFAULT_MAX_PER_MINUTE = 30
// Browsers reject a keepalive fetch once in-flight keepalive bodies exceed
// 64 KiB (shared with posthog-js's own beacons); posthog-js itself stops
// using keepalive above ~51 KiB. Bigger batches go out as a normal fetch.
export const KEEPALIVE_BODY_LIMIT_BYTES = 48_000

export interface PosthogLogShipperOptions {
  /** Public project token (`phc_…`) — the logs endpoint authenticates with it. */
  token: string
  /** Ingest host, e.g. https://us.i.posthog.com */
  host: string
  /** OTLP resource attributes; `service.name` is what the Logs UI groups by. */
  resource: Record<string, string>
  /** Called (never thrown) when a batch could not be delivered. */
  onFailure?: (reason: string) => void
  fetchImpl?: typeof fetch
  now?: () => number
  setTimer?: (fn: () => void, ms: number) => unknown
  clearTimer?: (handle: unknown) => void
  batchSize?: number
  flushIntervalMs?: number
  maxPerMinute?: number
}

type ResolvedOptions = Required<Omit<PosthogLogShipperOptions, 'onFailure'>> &
  Pick<PosthogLogShipperOptions, 'onFailure'>

export class PosthogLogShipper {
  private readonly cfg: ResolvedOptions
  private buffer: OtlpLogRecord[] = []
  private flushHandle: unknown = null
  private windowStart = 0
  private windowCount = 0
  private dropped = 0

  constructor(options: PosthogLogShipperOptions) {
    this.cfg = {
      fetchImpl: (...args) => fetch(...args),
      now: () => Date.now(),
      setTimer: (fn, ms) => setTimeout(fn, ms),
      clearTimer: handle => clearTimeout(handle as number),
      batchSize: DEFAULT_BATCH_SIZE,
      flushIntervalMs: DEFAULT_FLUSH_INTERVAL_MS,
      maxPerMinute: DEFAULT_MAX_PER_MINUTE,
      ...stripUndefined(options),
    } as ResolvedOptions
  }

  /** Records dropped by the per-minute cap since construction (observability for tests + debugging). */
  get droppedCount(): number {
    return this.dropped
  }

  ship(level: PosthogLogLevel, message: string, attributes: Record<string, unknown> = {}): void {
    try {
      const now = this.cfg.now()
      if (now - this.windowStart > 60_000) {
        this.windowStart = now
        this.windowCount = 0
      }
      if (++this.windowCount > this.cfg.maxPerMinute) {
        this.dropped++
        return
      }
      this.buffer.push(otlpLogRecord(level, message, attributes, now))
      if (this.buffer.length >= this.cfg.batchSize) {
        this.flush()
      } else if (!this.flushHandle) {
        this.flushHandle = this.cfg.setTimer(() => this.flush(), this.cfg.flushIntervalMs)
      }
    } catch {
      // Telemetry must never break the caller
    }
  }

  flush(): void {
    try {
      if (this.flushHandle) {
        this.cfg.clearTimer(this.flushHandle)
        this.flushHandle = null
      }
      if (this.buffer.length === 0) return
      const records = this.buffer
      this.buffer = []
      const body = JSON.stringify(otlpLogsPayload(records, this.cfg.resource, 'browser'))
      const result = this.cfg.fetchImpl(`${this.cfg.host}/i/v1/logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.cfg.token}`,
        },
        body,
        // keepalive lets a pagehide flush survive navigation, but only small
        // bodies are allowed to use it (see KEEPALIVE_BODY_LIMIT_BYTES)
        keepalive: body.length < KEEPALIVE_BODY_LIMIT_BYTES,
      })
      Promise.resolve(result)
        .then(response => {
          if (!response.ok) this.report(`HTTP ${response.status}`)
        })
        .catch(error => this.report(error instanceof Error ? error.message : String(error)))
    } catch (error) {
      // Never throw into the caller; a lost batch is not worth breaking the page
      this.report(error instanceof Error ? error.message : String(error))
    }
  }

  private report(reason: string): void {
    try {
      this.cfg.onFailure?.(reason)
    } catch {
      // The failure hook must not be able to break the shipper either
    }
  }
}

function stripUndefined<T extends object>(value: T): Partial<T> {
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as Partial<T>
}
